import { useEffect, useState, useCallback } from 'react';
import { HelpButton } from '../../components/HelpButton';
import { ConfirmDialog, Pagination } from '../../components';
import { staffPartnersService, mediaUrl, STAFF_PAGE_SIZE } from '../../lib/api-service';
import type { PartnerDetail } from '../../lib/types';
import { useAuth } from '../../lib/auth';
import {
  Search,
  Plus,
  Pencil,
  Trash2,
  Handshake,
  X,
  Loader2,
  Eye,
  EyeOff,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  AlertTriangle,
} from 'lucide-react';

const STATUS_LABELS: Record<string, string> = {
  public_entity: 'Instituție publică',
  private_company: 'Companie privată',
  ngo: 'ONG',
  informal_group: 'Grup informal',
};

const STATUS_OPTIONS = [
  { value: 'public_entity', label: 'Instituție publică' },
  { value: 'private_company', label: 'Companie privată' },
  { value: 'ngo', label: 'ONG' },
  { value: 'informal_group', label: 'Grup informal' },
];

type FormData = {
  name: string;
  description: string;
  entity_type: 'legal' | 'natural';
  legal_name: string;
  cif: string;
  legal_address: string;
  legal_representative_name: string;
  legal_representative_role: string;
  status: string;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  instagram: string;
  is_active: boolean;
  display_order: string;
};

const EMPTY_FORM: FormData = {
  name: '',
  description: '',
  entity_type: 'legal',
  legal_name: '',
  cif: '',
  legal_address: '',
  legal_representative_name: '',
  legal_representative_role: '',
  status: 'ngo',
  contact_name: '',
  contact_phone: '',
  contact_email: '',
  instagram: '',
  is_active: true,
  display_order: '0',
};

export default function ManagePartners() {
  const [partners, setPartners] = useState<PartnerDetail[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Form state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [form, setForm] = useState<FormData>(EMPTY_FORM);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [deleteTarget, setDeleteTarget] = useState<PartnerDetail | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<PartnerDetail | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [legalFieldsUnlocked, setLegalFieldsUnlocked] = useState(false);
  const [showUnlockConfirm, setShowUnlockConfirm] = useState(false);
  const { user } = useAuth();

  const loadPartners = useCallback(async () => {
    setLoading(true);
    try {
      const params: { page?: number; search?: string } = { page };
      if (searchQuery) params.search = searchQuery;
      const data = await staffPartnersService.list(params);
      setPartners(data.results);
      setTotalCount(data.count);
    } catch {
      setError('Nu s-au putut încărca prietenii.');
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery]);

  useEffect(() => { loadPartners(); }, [loadPartners]);
  useEffect(() => { setPage(1); }, [searchQuery]);

  function startAdd() {
    setIsAdding(true);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setLogoFile(null);
    setLogoPreview(null);
    setError('');
    setFieldErrors({});
    setShowPreview(false);
  }

  function startEdit(partner: PartnerDetail) {
    setEditingId(partner.id);
    setIsAdding(false);
    setLegalFieldsUnlocked(false);
    setForm({
      name: partner.name,
      description: partner.description,
      entity_type: partner.entity_type ?? 'legal',
      legal_name: partner.legal_name,
      cif: partner.cif,
      legal_address: partner.legal_address,
      legal_representative_name: partner.legal_representative_name,
      legal_representative_role: partner.legal_representative_role,
      status: partner.status,
      contact_name: partner.contact_name,
      contact_phone: partner.contact_phone,
      contact_email: partner.contact_email,
      instagram: partner.instagram || '',
      is_active: partner.is_active,
      display_order: String(partner.display_order ?? 0),
    });
    setLogoFile(null);
    setLogoPreview(mediaUrl(partner.logo) || null);
    setError('');
    setFieldErrors({});
    setShowPreview(false);
  }

  function cancelForm() {
    setIsAdding(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setLogoFile(null);
    setLogoPreview(null);
    setError('');
    setFieldErrors({});
    setShowPreview(false);
  }

  function handleLogoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  }

  function clearFieldError(field: string) {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
  }

  async function handleSave() {
    if (!form.name.trim()) {
      setFieldErrors({ name: 'Denumirea este obligatorie.' });
      setError('Denumirea este obligatorie.');
      return;
    }
    setSaving(true);
    setError('');
    setFieldErrors({});
    try {
      if (logoFile) {
        const fd = new FormData();
        fd.append('name', form.name);
        fd.append('description', form.description);
        fd.append('entity_type', form.entity_type);
        fd.append('legal_name', form.legal_name);
        fd.append('cif', form.cif);
        fd.append('legal_address', form.legal_address);
        fd.append('legal_representative_name', form.entity_type === 'natural' ? '' : form.legal_representative_name);
        fd.append('legal_representative_role', form.entity_type === 'natural' ? '' : form.legal_representative_role);
        fd.append('status', form.status);
        fd.append('contact_name', form.contact_name);
        fd.append('contact_phone', form.contact_phone);
        fd.append('contact_email', form.contact_email);
        fd.append('instagram', form.instagram);
        fd.append('is_active', String(form.is_active));
        fd.append('display_order', form.display_order || '0');
        fd.append('logo', logoFile);
        if (isAdding) {
          await staffPartnersService.create(fd);
        } else {
          await staffPartnersService.updateWithFile(editingId!, fd);
        }
      } else if (isAdding) {
        await staffPartnersService.create((() => {
          const fd = new FormData();
          fd.append('name', form.name);
          fd.append('description', form.description);
          fd.append('entity_type', form.entity_type);
          fd.append('legal_name', form.legal_name);
          fd.append('cif', form.cif);
          fd.append('legal_address', form.legal_address);
          fd.append('legal_representative_name', form.entity_type === 'natural' ? '' : form.legal_representative_name);
          fd.append('legal_representative_role', form.entity_type === 'natural' ? '' : form.legal_representative_role);
          fd.append('status', form.status);
          fd.append('contact_name', form.contact_name);
          fd.append('contact_phone', form.contact_phone);
          fd.append('contact_email', form.contact_email);
          fd.append('is_active', String(form.is_active));
        fd.append('display_order', form.display_order || '0');
          return fd;
        })());
      } else {
        const payload: Record<string, unknown> = {
          ...form,
          legal_representative_name: form.entity_type === 'natural' ? '' : form.legal_representative_name,
          legal_representative_role: form.entity_type === 'natural' ? '' : form.legal_representative_role,
          display_order: parseInt(form.display_order || '0', 10),
        };
        await staffPartnersService.update(editingId!, payload);
      }

      cancelForm();
      await loadPartners();
    } catch (err: any) {
      const body = err?.body;
      if (body && typeof body === 'object') {
        const fe: Record<string, string> = {};
        let top = '';
        for (const [k, v] of Object.entries(body)) {
          const msg = Array.isArray(v) ? (v as unknown[]).join(' ') : String(v);
          if (k === 'detail' || k === 'non_field_errors') {
            top = msg;
          } else {
            fe[k] = msg;
          }
        }
        setFieldErrors(fe);
        setError(top || (Object.keys(fe).length ? 'Verifică erorile evidențiate în formular.' : 'Eroare la salvare.'));
      } else {
        setError('Eroare la salvare. Verifică datele și încearcă din nou.');
      }
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    try {
      await staffPartnersService.delete(deleteTarget.id);
      setDeleteTarget(null);
      await loadPartners();
    } catch {
      setError('Eroare la ștergere.');
    }
  }

  async function confirmRevokeDpa() {
    if (!revokeTarget) return;
    try {
      await staffPartnersService.revokeDpa(revokeTarget.id);
      setRevokeTarget(null);
      await loadPartners();
    } catch {
      setError('Eroare la revocarea DPA.');
    }
  }

  async function toggleActive(partner: PartnerDetail) {
    try {
      await staffPartnersService.update(partner.id, { is_active: !partner.is_active });
      await loadPartners();
    } catch {
      setError('Eroare la actualizare.');
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--vivid-purple)]" />
      </div>
    );
  }

  const formOpen = isAdding || editingId !== null;
  const editingPartner = editingId ? partners.find((p) => p.id === editingId) : null;
  const legalFieldsLocked = !isAdding && !!editingPartner?.dpa_accepted_at && !legalFieldsUnlocked;

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
            Prieteni
          </h1>
          <HelpButton to="/staff/documentatie/parteneri" />
        </div>
        {!formOpen && (
          <button
            onClick={startAdd}
            className="flex items-center gap-2 rounded-lg bg-[var(--vivid-purple)] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--vivid-purple)]/90"
            style={{ fontFamily: 'var(--font-primary)' }}
          >
            <Plus className="h-4 w-4" />
            Prieten Nou
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {/* Add / Edit Form */}
      {formOpen && (
        <div className="mb-6 rounded-xl border bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
              {isAdding ? 'Prieten Nou' : 'Editează Prieten'}
            </h2>
            <button onClick={cancelForm} className="text-muted-foreground hover:text-foreground">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                Denumire *
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => { setForm({ ...form, name: e.target.value }); clearFieldError('name'); }}
                autoFocus
                aria-invalid={!!fieldErrors.name}
                className={`w-full rounded-lg border ${fieldErrors.name ? 'border-red-400' : ''} px-3 py-2.5 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20`}
                style={{ fontFamily: 'var(--font-secondary)' }}
              />
              {fieldErrors.name && <p className="mt-1 text-xs text-red-600">{fieldErrors.name}</p>}
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                Tip prieten
              </label>
              <div className="inline-flex rounded-lg border bg-white p-0.5">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, entity_type: 'legal' })}
                  disabled={legalFieldsLocked}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${
                    form.entity_type === 'legal' ? 'bg-[var(--vivid-purple)] text-white' : 'text-muted-foreground'
                  }`}
                >
                  Persoană juridică
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, entity_type: 'natural' })}
                  disabled={legalFieldsLocked}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${
                    form.entity_type === 'natural' ? 'bg-[var(--vivid-purple)] text-white' : 'text-muted-foreground'
                  }`}
                >
                  Persoană fizică
                </button>
              </div>
            </div>
            <div className="sm:col-span-2 flex items-center justify-between">
              <span className="text-sm font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>Date legale</span>
              {legalFieldsLocked && (
                <button
                  type="button"
                  onClick={() => setShowUnlockConfirm(true)}
                  className="flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-100"
                >
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Editează datele legale
                </button>
              )}
            </div>
            {legalFieldsLocked ? (
              <div className="sm:col-span-2 grid grid-cols-1 gap-2 rounded-lg border bg-gray-50 p-4 sm:grid-cols-2">
                <div>
                  <span className="text-xs text-muted-foreground">
                    {form.entity_type === 'natural' ? 'Nume complet' : 'Denumire legală'}
                  </span>
                  <p className="text-sm font-medium">{editingPartner?.legal_name || '—'}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">
                    {form.entity_type === 'natural' ? 'CNP' : 'CIF / CUI'}
                  </span>
                  <p className="text-sm font-medium">{editingPartner?.cif || '—'}</p>
                </div>
                <div className="sm:col-span-2">
                  <span className="text-xs text-muted-foreground">
                    {form.entity_type === 'natural' ? 'Adresă' : 'Sediu social'}
                  </span>
                  <p className="text-sm font-medium">{editingPartner?.legal_address || '—'}</p>
                </div>
                {form.entity_type !== 'natural' && (
                  <>
                    <div>
                      <span className="text-xs text-muted-foreground">Reprezentant legal</span>
                      <p className="text-sm font-medium">{editingPartner?.legal_representative_name || '—'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Funcția</span>
                      <p className="text-sm font-medium">{editingPartner?.legal_representative_role || '—'}</p>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                {legalFieldsUnlocked && (
                  <div className="sm:col-span-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                    Modificarea datelor legale va invalida DPA-ul semnat de prieten.
                  </div>
                )}
                <div>
                  <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                    {form.entity_type === 'natural' ? 'Nume complet' : 'Denumire legală'}
                  </label>
                  <input
                    type="text"
                    value={form.legal_name}
                    onChange={(e) => { setForm({ ...form, legal_name: e.target.value }); clearFieldError('legal_name'); }}
                    aria-invalid={!!fieldErrors.legal_name}
                    className={`w-full rounded-lg border ${fieldErrors.legal_name ? 'border-red-400' : ''} px-3 py-2.5 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20`}
                    style={{ fontFamily: 'var(--font-secondary)' }}
                  />
                  {fieldErrors.legal_name && <p className="mt-1 text-xs text-red-600">{fieldErrors.legal_name}</p>}
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                    {form.entity_type === 'natural' ? 'CNP' : 'CIF / CUI'}
                  </label>
                  <input
                    type="text"
                    value={form.cif}
                    onChange={(e) => { setForm({ ...form, cif: e.target.value }); clearFieldError('cif'); }}
                    inputMode={form.entity_type === 'natural' ? 'numeric' : 'text'}
                    maxLength={form.entity_type === 'natural' ? 13 : 13}
                    aria-invalid={!!fieldErrors.cif}
                    className={`w-full rounded-lg border ${fieldErrors.cif ? 'border-red-400' : ''} px-3 py-2.5 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20`}
                    style={{ fontFamily: 'var(--font-secondary)' }}
                  />
                  {fieldErrors.cif && <p className="mt-1 text-xs text-red-600">{fieldErrors.cif}</p>}
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                    {form.entity_type === 'natural' ? 'Adresă' : 'Sediu social'}
                  </label>
                  <input
                    type="text"
                    value={form.legal_address}
                    onChange={(e) => { setForm({ ...form, legal_address: e.target.value }); clearFieldError('legal_address'); }}
                    aria-invalid={!!fieldErrors.legal_address}
                    className={`w-full rounded-lg border ${fieldErrors.legal_address ? 'border-red-400' : ''} px-3 py-2.5 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20`}
                    style={{ fontFamily: 'var(--font-secondary)' }}
                  />
                  {fieldErrors.legal_address && <p className="mt-1 text-xs text-red-600">{fieldErrors.legal_address}</p>}
                </div>
                {form.entity_type !== 'natural' && (
                  <>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                        Reprezentant legal
                      </label>
                      <input
                        type="text"
                        value={form.legal_representative_name}
                        onChange={(e) => { setForm({ ...form, legal_representative_name: e.target.value }); clearFieldError('legal_representative_name'); }}
                        placeholder="Nume complet"
                        aria-invalid={!!fieldErrors.legal_representative_name}
                        className={`w-full rounded-lg border ${fieldErrors.legal_representative_name ? 'border-red-400' : ''} px-3 py-2.5 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20`}
                        style={{ fontFamily: 'var(--font-secondary)' }}
                      />
                      {fieldErrors.legal_representative_name && <p className="mt-1 text-xs text-red-600">{fieldErrors.legal_representative_name}</p>}
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                        Funcție reprezentant
                      </label>
                      <input
                        type="text"
                        value={form.legal_representative_role}
                        onChange={(e) => { setForm({ ...form, legal_representative_role: e.target.value }); clearFieldError('legal_representative_role'); }}
                        placeholder="Ex: Președinte, Administrator"
                        aria-invalid={!!fieldErrors.legal_representative_role}
                        className={`w-full rounded-lg border ${fieldErrors.legal_representative_role ? 'border-red-400' : ''} px-3 py-2.5 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20`}
                        style={{ fontFamily: 'var(--font-secondary)' }}
                      />
                      {fieldErrors.legal_representative_role && <p className="mt-1 text-xs text-red-600">{fieldErrors.legal_representative_role}</p>}
                    </div>
                  </>
                )}
              </>
            )}
            {form.entity_type !== 'natural' && (
              <div>
                <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                  Tip organizație
                </label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
                  style={{ fontFamily: 'var(--font-secondary)' }}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="sm:col-span-2">
              <div className="mb-1.5 flex items-center justify-between">
                <label className="text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                  Descriere (Markdown)
                </label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowPreview(!showPreview)}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                  >
                    {showPreview ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    {showPreview ? 'Editare' : 'Previzualizare'}
                  </button>
                  <a href="/staff/documentatie/markdown" target="_blank" rel="noopener noreferrer" className="flex h-5 w-5 items-center justify-center rounded-full border text-xs text-muted-foreground hover:border-[var(--vivid-purple)] hover:text-[var(--vivid-purple)]" title="Ghid formatare Markdown">?</a>
                </div>
              </div>
              {showPreview ? (
                <div
                  className="min-h-[120px] rounded-lg border bg-gray-50 px-3 py-2.5 text-sm prose prose-sm max-w-none"
                  style={{ fontFamily: 'var(--font-secondary)' }}
                  dangerouslySetInnerHTML={{
                    __html: form.description
                      .replace(/\n/g, '<br>')
                      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                      .replace(/\*(.*?)\*/g, '<em>$1</em>')
                      .replace(/^## (.*$)/gm, '<h3>$1</h3>')
                      .replace(/^# (.*$)/gm, '<h2>$1</h2>'),
                  }}
                />
              ) : (
                <textarea
                  value={form.description}
                  onChange={(e) => { setForm({ ...form, description: e.target.value }); clearFieldError('description'); }}
                  rows={5}
                  aria-invalid={!!fieldErrors.description}
                  className={`w-full rounded-lg border ${fieldErrors.description ? 'border-red-400' : ''} px-3 py-2.5 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20`}
                  style={{ fontFamily: 'var(--font-secondary)' }}
                />
              )}
              {fieldErrors.description && <p className="mt-1 text-xs text-red-600">{fieldErrors.description}</p>}
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                Logo
              </label>
              <div className="flex items-center gap-4">
                {logoPreview && (
                  <img src={logoPreview} alt="Logo preview" className="h-16 w-16 rounded-lg border object-cover" />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => { handleLogoChange(e); clearFieldError('logo'); }}
                  aria-invalid={!!fieldErrors.logo}
                  className="text-sm text-muted-foreground file:mr-3 file:rounded-lg file:border-0 file:bg-[var(--vivid-purple)]/10 file:px-3 file:py-2 file:text-sm file:font-medium file:text-[var(--vivid-purple)]"
                />
              </div>
              {fieldErrors.logo && <p className="mt-1 text-xs text-red-600">{fieldErrors.logo}</p>}
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                Persoană de contact
              </label>
              <input
                type="text"
                value={form.contact_name}
                onChange={(e) => { setForm({ ...form, contact_name: e.target.value }); clearFieldError('contact_name'); }}
                aria-invalid={!!fieldErrors.contact_name}
                className={`w-full rounded-lg border ${fieldErrors.contact_name ? 'border-red-400' : ''} px-3 py-2.5 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20`}
                style={{ fontFamily: 'var(--font-secondary)' }}
              />
              {fieldErrors.contact_name && <p className="mt-1 text-xs text-red-600">{fieldErrors.contact_name}</p>}
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                Telefon contact
              </label>
              <input
                type="tel"
                value={form.contact_phone}
                onChange={(e) => { setForm({ ...form, contact_phone: e.target.value }); clearFieldError('contact_phone'); }}
                aria-invalid={!!fieldErrors.contact_phone}
                className={`w-full rounded-lg border ${fieldErrors.contact_phone ? 'border-red-400' : ''} px-3 py-2.5 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20`}
                style={{ fontFamily: 'var(--font-secondary)' }}
              />
              {fieldErrors.contact_phone && <p className="mt-1 text-xs text-red-600">{fieldErrors.contact_phone}</p>}
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                Email contact
              </label>
              <input
                type="email"
                value={form.contact_email}
                onChange={(e) => { setForm({ ...form, contact_email: e.target.value }); clearFieldError('contact_email'); }}
                aria-invalid={!!fieldErrors.contact_email}
                className={`w-full rounded-lg border ${fieldErrors.contact_email ? 'border-red-400' : ''} px-3 py-2.5 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20`}
                style={{ fontFamily: 'var(--font-secondary)' }}
              />
              {fieldErrors.contact_email && <p className="mt-1 text-xs text-red-600">{fieldErrors.contact_email}</p>}
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                Instagram
              </label>
              <input
                type="text"
                value={form.instagram}
                onChange={(e) => setForm({ ...form, instagram: e.target.value })}
                placeholder="@nume_cont sau link complet"
                className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
                style={{ fontFamily: 'var(--font-secondary)' }}
              />
            </div>
            <div className="flex items-center">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="h-4 w-4 rounded"
                  style={{ accentColor: 'var(--vivid-purple)' }}
                />
                <span className="text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                  Activ
                </span>
              </label>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                Ordine afișare
              </label>
              <input
                type="number"
                min="0"
                value={form.display_order}
                onChange={(e) => { setForm({ ...form, display_order: e.target.value }); clearFieldError('display_order'); }}
                aria-invalid={!!fieldErrors.display_order}
                className={`w-full rounded-lg border ${fieldErrors.display_order ? 'border-red-400' : ''} px-3 py-2.5 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20`}
                style={{ fontFamily: 'var(--font-secondary)' }}
              />
              {fieldErrors.display_order && <p className="mt-1 text-xs text-red-600">{fieldErrors.display_order}</p>}
              <p className="mt-1 text-xs text-muted-foreground">Număr mai mic apare primul în lista publică.</p>
            </div>
          </div>
          <div className="mt-5 flex gap-2">
            <button
              onClick={cancelForm}
              className="rounded-lg border px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-gray-50"
              style={{ fontFamily: 'var(--font-primary)' }}
            >
              Anulează
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="rounded-lg bg-[var(--vivid-purple)] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--vivid-purple)]/90 disabled:opacity-60"
              style={{ fontFamily: 'var(--font-primary)' }}
            >
              {saving ? 'Se salvează...' : isAdding ? 'Adaugă' : 'Salvează'}
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            placeholder="Caută prieteni..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border py-2.5 pl-10 pr-4 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
            style={{ fontFamily: 'var(--font-secondary)' }}
          />
        </div>
      </div>

      {/* Partners Table */}
      <div className="overflow-hidden rounded-xl border bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50/50">
              <th className="px-4 py-3 text-left font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>Prieten</th>
              <th className="hidden px-4 py-3 text-left font-semibold sm:table-cell" style={{ fontFamily: 'var(--font-primary)' }}>Tip</th>
              <th className="px-4 py-3 text-left font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>Status</th>
              <th className="px-4 py-3 text-right font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>Acțiuni</th>
            </tr>
          </thead>
          <tbody>
            {partners.map((partner) => (
              <tr key={partner.id} className="border-b last:border-b-0 hover:bg-gray-50/50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {partner.logo ? (
                      <img src={mediaUrl(partner.logo)} alt="" className="h-9 w-9 rounded-lg border object-cover" />
                    ) : (
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--vivid-purple)]/10">
                        <Handshake className="h-4 w-4 text-[var(--vivid-purple)]" />
                      </div>
                    )}
                    <div>
                      <div className="font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                        {partner.name}
                      </div>
                      {partner.legal_name && (
                        <div className="text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                          {partner.legal_name}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="hidden px-4 py-3 sm:table-cell">
                  <div className="flex items-center gap-1.5">
                    <span
                      className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                        partner.entity_type === 'natural'
                          ? 'bg-[var(--vivid-cyan)]/20 text-cyan-900'
                          : 'bg-[var(--vivid-purple)]/10 text-[var(--vivid-purple)]'
                      }`}
                      title={partner.entity_type === 'natural' ? 'Persoană fizică' : 'Persoană juridică'}
                    >
                      {partner.entity_type === 'natural' ? 'PF' : 'PJ'}
                    </span>
                    <span className="text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                      {partner.entity_type === 'natural' ? '—' : (STATUS_LABELS[partner.status] || partner.status)}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      onClick={() => toggleActive(partner)}
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
                        partner.is_active
                          ? 'bg-green-50 text-green-700 hover:bg-green-100'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                      title={partner.is_active ? 'Click pentru dezactivare' : 'Click pentru activare'}
                    >
                      {partner.is_active ? 'Activ' : 'Inactiv'}
                    </button>
                    {partner.is_validated ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
                        <ShieldCheck className="h-3 w-3" /> Validat
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
                        <ShieldAlert className="h-3 w-3" /> Nevalidat
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    {user?.is_admin && partner.is_validated && partner.dpa_accepted_at && (
                      <button
                        onClick={() => setRevokeTarget(partner)}
                        className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-amber-50 hover:text-amber-600"
                        title="Revocă DPA"
                      >
                        <ShieldX className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => startEdit(partner)}
                      className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-gray-100 hover:text-[var(--vivid-purple)]"
                      title="Editează"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(partner)}
                      className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600"
                      title="Șterge"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {partners.length === 0 && (
          <div className="flex flex-col items-center py-12 text-center">
            <Handshake className="mb-3 h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
              Nu am găsit prieteni
            </p>
          </div>
        )}
      </div>

      <Pagination page={page} totalCount={totalCount} pageSize={STAFF_PAGE_SIZE} onPageChange={setPage} />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Șterge prietenul"
        description={`Sigur vrei să ștergi prietenul „${deleteTarget?.name}"? Acțiunea este ireversibilă.`}
      />

      <ConfirmDialog
        open={!!revokeTarget}
        onOpenChange={(open) => !open && setRevokeTarget(null)}
        onConfirm={confirmRevokeDpa}
        title="Revocă DPA"
        description={`Sigur vrei să revoci Acordul de Prelucrare a Datelor pentru „${revokeTarget?.name}"? Prietenul va pierde accesul la listele de participanți.`}
      />

      <ConfirmDialog
        open={showUnlockConfirm}
        onOpenChange={(open) => !open && setShowUnlockConfirm(false)}
        onConfirm={() => {
          setLegalFieldsUnlocked(true);
          setShowUnlockConfirm(false);
        }}
        title="Editează datele legale"
        description="Modificarea datelor legale va invalida DPA-ul semnat de prieten. Prietenul va trebui să re-semneze acordul pentru a avea din nou acces la datele participanților."
      />
    </div>
  );
}
